import {
  type InfiniteData,
  type QueryKey,
  useInfiniteQuery,
} from "@tanstack/react-query";

import { useDebouncedValue } from "~/v2/hooks/useDebouncedValue";
import { queryKeys } from "../queryKeys";
import {
  type AssetClass,
  type AssetExchange,
  type AssetSearchResponse,
  type AssetStatus,
  getAssetsViaFullTextSearch,
} from "../rest";

export interface UseFullTextAssetSearchOptions {
  limit?: number;
  status?: AssetStatus;
  assetClass?: AssetClass;
  exchanges?: AssetExchange[];
  debounceMs?: number;
  enabled?: boolean;
}

export const useFullTextAssetSearchInfinite = ({
  query,
  limit = 10,
  status = "active",
  assetClass,
  exchanges,
  debounceMs = 300,
  enabled = true,
}: { query: string } & UseFullTextAssetSearchOptions) => {
  const debouncedQuery = useDebouncedValue(query, debounceMs);

  return useInfiniteQuery<
    AssetSearchResponse,
    Error,
    InfiniteData<AssetSearchResponse>,
    QueryKey,
    number
  >({
    queryKey: queryKeys.assets.search(
      debouncedQuery,
      {
        status,
        assetClass,
        exchanges: exchanges?.join(","),
      },
      limit,
    ),
    queryFn: async ({ pageParam }) => {
      return getAssetsViaFullTextSearch(
        debouncedQuery,
        limit,
        pageParam,
        status,
        assetClass,
        exchanges,
      );
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const lastPageLength = lastPage.assets?.length ?? 0;
      if (lastPageLength < limit) {
        return undefined;
      }
      return allPages.length * limit;
    },

    enabled: enabled && debouncedQuery.trim().length > 0,
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });
};
